import type { TrackPoint, ElevationPoint } from '../types';

/**
 * Calculates distance in meters between two lat/lon coordinates using the Haversine formula.
 */
export const getDistanceMeters = (p1: [number, number], p2: [number, number]): number => {
    const R = 6371000;
    const dLat = (p2[0] - p1[0]) * Math.PI / 180;
    const dLon = (p2[1] - p1[1]) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(p1[0] * Math.PI / 180) * Math.cos(p2[0] * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

/**
 * Finds the index of the nearest point in a path to the given coordinates.
 */
export const findNearestPathIndex = (
    lat: number,
    lon: number,
    path: [number, number][],
    startIndex = 0,
    endIndex?: number
): { index: number; distanceMeters: number } => {
    if (!path || path.length === 0) return { index: -1, distanceMeters: Infinity };
    
    const maxIdx = endIndex !== undefined ? Math.min(path.length, endIndex) : path.length;
    let minDistance = Infinity;
    let bestIdx = -1;

    for (let i = startIndex; i < maxIdx; i++) {
        const pt = path[i];
        const dist = getDistanceMeters([lat, lon], pt);
        if (dist < minDistance) {
            minDistance = dist;
            bestIdx = i;
        }
    }

    return { index: bestIdx, distanceMeters: minDistance };
};

export interface RouteTrimResult {
    trimmedPath: [number, number][];
    trimmedTrack?: TrackPoint[];
    distanceMiles: number;
    elevationGainFt?: number;
    elevationProfile: ElevationPoint[];
    startIdx: number;
    endIdx: number;
    isLoop: boolean;
}

/**
 * Trims a route path (and optional full telemetry track) between designated start and finish coordinates.
 * Supports both Loop routes (where start and finish are the same meetup location) and Point-to-Point routes.
 */
export const trimRouteSegment = (
    path: [number, number][],
    startCoord: [number, number],
    finishCoord: [number, number],
    track?: TrackPoint[],
    forceLoop?: boolean
): RouteTrimResult | null => {
    if (!path || path.length < 5) return null;
    if (!startCoord || (startCoord[0] === 0 && startCoord[1] === 0)) return null;
    if (!finishCoord || (finishCoord[0] === 0 && finishCoord[1] === 0)) return null;

    const startFinishDistMeters = getDistanceMeters(startCoord, finishCoord);
    const isLoop = forceLoop !== undefined 
        ? forceLoop 
        : (startFinishDistMeters < 150 || (Math.abs(startCoord[0] - finishCoord[0]) < 0.0008 && Math.abs(startCoord[1] - finishCoord[1]) < 0.0008));

    let startIdx = -1;
    let endIdx = -1;

    if (isLoop) {
        // --- LOOP DETECTION ---
        // 1. Find the first arrival at the meetup / start area
        // Scan for the first local minimum or arrival within 250m of startCoord
        const firstArrival = findNearestPathIndex(startCoord[0], startCoord[1], path);
        if (firstArrival.index === -1) return null;

        // In a door-to-door file (e.g. 85 miles), the rider first arrives at meetup,
        // then departs on the loop (traveling kilometers away), then returns to finish.
        // Let's identify the first cluster where distance to start < 250m
        let entryIdx = -1;
        for (let i = 0; i < path.length; i++) {
            if (getDistanceMeters(startCoord, path[i]) < 250) {
                entryIdx = i;
                break;
            }
        }

        // If not found within 250m, fall back to the global nearest point
        if (entryIdx === -1) {
            entryIdx = firstArrival.index;
        } else {
            // Find the closest point in this initial arrival cluster (before departure)
            let bestDist = getDistanceMeters(startCoord, path[entryIdx]);
            let bestIdx = entryIdx;
            while (entryIdx < path.length - 1 && getDistanceMeters(startCoord, path[entryIdx]) < 500) {
                const dist = getDistanceMeters(startCoord, path[entryIdx]);
                if (dist < bestDist) {
                    bestDist = dist;
                    bestIdx = entryIdx;
                }
                entryIdx++;
            }
            entryIdx = bestIdx;
        }

        startIdx = entryIdx;

        // 2. Find when the rider leaves the start area (distance > 600m)
        let departedIdx = -1;
        for (let i = startIdx; i < path.length; i++) {
            if (getDistanceMeters(startCoord, path[i]) > 600) {
                departedIdx = i;
                break;
            }
        }

        // 3. Find when the rider returns to the finish/meetup area after completing the loop
        if (departedIdx !== -1) {
            // Scan after departure for return to finishCoord
            let returnSearchIdx = departedIdx;
            let bestReturnDist = Infinity;
            let returnIdx = -1;

            for (let i = returnSearchIdx; i < path.length; i++) {
                const dist = getDistanceMeters(finishCoord, path[i]);
                if (dist < 300 && dist < bestReturnDist) {
                    bestReturnDist = dist;
                    returnIdx = i;
                }
            }

            // If found a return within 300m, hone in on the closest point in that return window
            if (returnIdx !== -1) {
                endIdx = returnIdx;
            } else {
                // Search for overall nearest point to finishCoord after departure
                const postDepartNearest = findNearestPathIndex(finishCoord[0], finishCoord[1], path, departedIdx);
                if (postDepartNearest.index !== -1 && postDepartNearest.index > startIdx) {
                    endIdx = postDepartNearest.index;
                }
            }
        }

        // Fallback: if no distinct departure/return found (e.g. already a clean loop), use entire path
        if (endIdx === -1 || endIdx <= startIdx) {
            // The file itself might already be just the loop, or start was near the beginning
            if (startIdx < path.length / 2) {
                const lastNearest = findNearestPathIndex(finishCoord[0], finishCoord[1], path, Math.floor(path.length * 0.75));
                if (lastNearest.index !== -1 && lastNearest.index > startIdx) {
                    endIdx = lastNearest.index;
                } else {
                    endIdx = path.length - 1;
                }
            } else {
                startIdx = 0;
                endIdx = path.length - 1;
            }
        }

    } else {
        // --- POINT TO POINT DETECTION ---
        const startMatch = findNearestPathIndex(startCoord[0], startCoord[1], path);
        startIdx = startMatch.index !== -1 ? startMatch.index : 0;

        // Search for finish point AFTER the start point
        const finishMatch = findNearestPathIndex(finishCoord[0], finishCoord[1], path, startIdx);
        endIdx = finishMatch.index !== -1 && finishMatch.index > startIdx ? finishMatch.index : path.length - 1;
    }

    // Safety checks
    if (startIdx < 0) startIdx = 0;
    if (endIdx <= startIdx || endIdx >= path.length) endIdx = path.length - 1;

    // Slice path
    const trimmedPath = path.slice(startIdx, endIdx + 1);

    // Slice track if available
    const hasTrack = Boolean(track && track.length === path.length);
    const trimmedTrack = hasTrack && track ? track.slice(startIdx, endIdx + 1) : undefined;

    // Compute distance
    let totalDistMeters = 0;
    for (let i = 0; i < trimmedPath.length - 1; i++) {
        totalDistMeters += getDistanceMeters(trimmedPath[i], trimmedPath[i + 1]);
    }
    const distanceMiles = Number((totalDistMeters * 0.000621371).toFixed(1));

    // Compute elevation gain & profile
    let elevationGainFt: number | undefined = undefined;
    const elevationProfile: ElevationPoint[] = [];

    if (trimmedTrack && trimmedTrack.length > 0) {
        let elevGainMeters = 0;
        let cumulativeDistMeters = 0;
        const hasAnyElevation = trimmedTrack.some(p => p.elevation !== 0);

        const sampleStep = Math.max(1, Math.floor(trimmedTrack.length / 150));

        for (let i = 0; i < trimmedTrack.length; i++) {
            const pt = trimmedTrack[i];
            if (i > 0) {
                const prev = trimmedTrack[i - 1];
                const elevDiff = pt.elevation - prev.elevation;
                if (elevDiff > 0.05) elevGainMeters += elevDiff;
                cumulativeDistMeters += getDistanceMeters([prev.lat, prev.lon], [pt.lat, pt.lon]);
            }

            if (i % sampleStep === 0 || i === trimmedTrack.length - 1) {
                elevationProfile.push({
                    distanceMiles: Number((cumulativeDistMeters * 0.000621371).toFixed(2)),
                    elevationFt: Math.round(pt.elevation * 3.28084),
                    lat: pt.lat,
                    lon: pt.lon
                });
            }
        }

        if (hasAnyElevation) {
            elevationGainFt = Math.round(elevGainMeters * 3.28084);
        }
    } else {
        // Build basic elevation profile from distance steps if no elevation available
        let cumulativeDistMeters = 0;
        const sampleStep = Math.max(1, Math.floor(trimmedPath.length / 150));
        for (let i = 0; i < trimmedPath.length; i++) {
            if (i > 0) {
                cumulativeDistMeters += getDistanceMeters(trimmedPath[i - 1], trimmedPath[i]);
            }
            if (i % sampleStep === 0 || i === trimmedPath.length - 1) {
                elevationProfile.push({
                    distanceMiles: Number((cumulativeDistMeters * 0.000621371).toFixed(2)),
                    elevationFt: 0,
                    lat: trimmedPath[i][0],
                    lon: trimmedPath[i][1]
                });
            }
        }
    }

    return {
        trimmedPath,
        trimmedTrack,
        distanceMiles,
        elevationGainFt,
        elevationProfile,
        startIdx,
        endIdx,
        isLoop
    };
};
